import { cn } from "@/lib/utils";
import { MoveHorizontal, PencilLine } from "lucide-react";
import type { ReactNode } from "react";
import { TextChip } from "./chip";

export interface SubHeaderFile {
  /** 파일 앞 태그 ("이전", "최신", "IXD", "DSD 워크북"). 색은 순서로 정해진다 */
  tag?: string;
  name: string;
  /** 파일정보 블록 옆 부가 표시 ("1개 수정. ‘저장’ 버튼을 눌러주세요." 등) */
  addon?: ReactNode;
  /**
   * 파일명 **아래**에 띄우는 안내 (절대 위치로 배치한다).
   * 기준점이 파일명 시작 지점이라 이름 element 를 감싼 relative 박스 안에 둔다.
   */
  nameHint?: ReactNode;
  /** 이 파일 슬롯 오른쪽 끝 (스위치, 워크북 변경 버튼 등) */
  actions?: ReactNode;
}

/**
 * 파일명 **옆** 수정 안내 라벨 (Figma subHeader 8877:795342 "IXD 미리보기" > 한글라벨).
 *
 * 파일정보(태그 + 파일명) 블록 바깥에 형제로 놓이고 간격은 16px 이다 — 디자인에서도
 * `wrap_fileinfo` 와 나란한 `file` 의 자식이다. 호출부가 `addon` 으로 넘기면 `SubHeader` 가
 * 그 자리에 둔다. 예전 `editNoti`(회색 info 칩)는 디자인에서 숨김 처리돼 쓰지 않는다.
 *
 * 문장형 안내라(`N개 수정. ‘저장’ 버튼을 눌러주세요.`) 줄바꿈을 막는다 — 이 줄은 높이가
 * 48px 로 고정이라 두 줄이 되면 넘친다. 대신 `min-w-0` + 글자 `truncate` 로 줄어들 수는
 * 있어야 한다. 2칸 모드에서 칸은 `w-1/2` 로 고정이라, 줄지 않으면 좁은 창에서 칸 밖으로
 * **겹쳐 그려진다**(파일명 아래 안내가 절대 위치라 조상에 `overflow-hidden` 도 못 건다).
 * 줄어드는 순서(파일명 먼저, 안내는 마지막)는 파일정보 블록의 `shrink-[1000]` 이 만든다.
 */
export const SubHeaderNote = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex min-w-0 items-center gap-1 rounded-[8px] bg-neutral-100 px-1.5 py-1 caption-2-medium text-neutral-700">
    <PencilLine aria-hidden className="size-4 shrink-0" />
    <span className="truncate">{children}</span>
  </span>
);

interface SubHeaderProps {
  /**
   * 파일 하나 = 단일, 둘 = 줄을 **정확히 반**으로 나누고 경계에 ↔ 를 띄운다
   * (Figma subHeader file=single/double). 두 칸은 본문의 좌·우 열 위에 얹힌다.
   */
  files: [SubHeaderFile] | [SubHeaderFile, SubHeaderFile];
  /** 줄 오른쪽 끝 (단일 파일의 "워크북 값 불러오기" 등) */
  actions?: ReactNode;
  className?: string;
}

/**
 * 파일이 둘이면 이 줄은 본문의 좌·우 열 위에 얹힌다 — 칸을 정확히 반으로 나누고
 * 화살표를 그 경계(= 본문 두 열 사이 거터의 중심)에 띄워 제목이 열과 어긋나지 않게 한다.
 *
 * 칸 안쪽 여백은 본문 카드의 글자 시작점에서 나온 값이다
 * (스크롤 영역 px-4 16 + 카드 테두리 1 + 카드 px-5 20 = 37, 오른쪽은 거터 절반 4 를 더해 25).
 * 본문 여백을 바꾸면 이 두 값도 같이 바꿔야 한다.
 */
const SPLIT_SLOT_CLASS = ["w-1/2 pl-[37px] pr-6", "w-1/2 pl-[25px] pr-4"];

// 첫 파일은 중립, 둘째는 강조 — 페이지가 색을 고르지 않는다.
// tag-* 는 Figma subHeader 의 slate/200·indigo/100·indigo/700 을 용도명으로 등록한 토큰(tokens/colors.css).
const TAG_TONE = [
  "bg-tag-neutral text-label-neutral",
  "bg-tag-accent text-tag-accent-label",
];

/**
 * 상단 바 아래 48px 파일 정보 줄 (Figma subHeader). MainHeader 와 짝이지만 별개 컴포넌트 —
 * 페이지에 따라 문서 컬럼 위에만 놓이기도 해서 한 DOM 으로 묶지 않는다.
 */
export const SubHeader = ({ files, actions, className }: SubHeaderProps) => {
  const isSplit = files.length === 2;

  return (
    <div
      className={cn(
        "relative flex h-12 shrink-0 items-center border-b border-line-normal bg-white py-2",
        isSplit ? "" : "gap-4 pl-6 pr-4",
        className,
      )}
    >
      {isSplit && (
        <MoveHorizontal
          data-testid="sub-header-arrow"
          className="pointer-events-none absolute left-1/2 top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 text-label-normal"
          aria-hidden="true"
        />
      )}
      {files.map((file, index) => (
        <div
          key={index}
          data-file-slot={file.name}
          className={cn(
            "flex min-w-0 items-center gap-4",
            isSplit ? SPLIT_SLOT_CLASS[index] : "flex-1",
          )}
        >
          {/*
            자리가 부족하면 **파일명이 먼저** 줄어든다 — 수정 안내(SubHeaderNote)도 줄어들
            수 있어야 칸 밖으로 안 넘치지만(`min-w-0`), 수축량은 `shrink × 기본폭` 비례라
            그대로 두면 긴 파일명 옆에서 안내 문장이 먼저 말줄임된다. 큰 shrink 로 몫을 이
            블록이 거의 다 받고, 파일명이 0 이 되면 남은 몫이 안내로 간다.
          */}
          <div className="flex min-w-0 shrink-[1000] items-center gap-2">
            {file.tag && (
              <TextChip size="large" className={TAG_TONE[index]}>
                {file.tag}
              </TextChip>
            )}
            <div className="relative min-w-0">
              <p className="label-1-normal-medium truncate text-label-normal">
                {file.name}
              </p>
              {file.nameHint}
            </div>
          </div>
          {/* 파일정보 블록과 16px (슬롯의 gap-4) — Figma `file` 의 자식 간격 그대로 */}
          {file.addon}
          {file.actions && (
            <div className="flex flex-1 items-center justify-end gap-4">
              {file.actions}
            </div>
          )}
        </div>
      ))}
      {actions && (
        <div className="flex shrink-0 items-center gap-4">{actions}</div>
      )}
    </div>
  );
};
